import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { unionBy } from 'lodash';
import { EMPTY } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';

import { untilDestroyed, UrlParamsService } from '@app/core';
import { ApiToken, OAuthToken, User } from '@app/models';
import { ApiTokenService, AppService, EnterpriseService, ErrorHandlerService, NotyService, OAuthService, UserService } from '@app/services';
import { SetMissingUserInfoModalService } from '@app/shared';

@Component({
  selector: 'app-configurations-api',
  templateUrl: './configurations-api.component.html',
  styleUrls: ['../configurations.scss', './configurations-api.component.scss'],
  standalone: false
})
export class ConfigurationsApiComponent implements OnInit, OnDestroy {
  currentUser: User;
  apiTokens: ApiToken[] = [];
  oauthTokens: OAuthToken[] = [];
  isLoading = false;
  isLoadingOAuthTokens = false;
  oauthTokensIsLastPage = false;
  oauthTokensPageNumber = 1;
  readonly oauthTokensPerPage = 30;
  isEditingApiOptions = false;

  constructor(
    public appService: AppService,
    public urlParamsService: UrlParamsService,
    public enterpriseService: EnterpriseService,
    private errorHandlerService: ErrorHandlerService,
    private apiTokenService: ApiTokenService,
    private oauthService: OAuthService,
    private userService: UserService,
    private notyService: NotyService,
    private translateService: TranslateService,
    private setMissingUserInfoModalService: SetMissingUserInfoModalService
  ) {}

  ngOnInit() {
    this.apiTokenService.all().subscribe({
      next: tokens => (this.apiTokens = tokens || []),
      error: error => this.errorHandlerService.handle(error)
    });

    this.loadOAuthTokens();

    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => (this.currentUser = user)
      });
  }

  ngOnDestroy() {}

  createApiToken() {
    this.isLoading = true;

    this.setMissingUserInfoModalService
      .openIfMissingUserInfo({
        ignoreCpf: true,
        ignoreBirthdate: true,
        dismissAsError: true
      })
      .pipe(
        take(1),
        switchMap(() => this.apiTokenService.create()),
        tap(tokens => {
          this.apiTokens = tokens.concat(this.apiTokens);
        }),
        catchError(() => {
          this.notyService.error(this.translateService.instant('settings.fillDataAPI'), 2000);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  deleteApiToken(apiAccessToken: string) {
    this.isLoading = true;

    this.apiTokenService
      .delete({ accessToken: apiAccessToken })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => (this.apiTokens = this.apiTokens.filter(token => token.access_token !== apiAccessToken)),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  loadOAuthTokens(options: { page?: number } = {}) {
    this.isLoadingOAuthTokens = true;
    this.oauthTokensPageNumber = options.page || 1;

    this.oauthService
      .tokens({ limit: this.oauthTokensPerPage, page: this.oauthTokensPageNumber })
      .pipe(finalize(() => (this.isLoadingOAuthTokens = false)))
      .subscribe({
        next: page => {
          const tokens = (page?.data || []).filter((token): token is OAuthToken => !!token);

          this.oauthTokens = unionBy(this.oauthTokensPageNumber === 1 ? [] : this.oauthTokens, tokens, 'id');
          this.oauthTokensIsLastPage = !page || !page.has_more_pages;
        },
        error: (error: unknown) => this.errorHandlerService.handle(error)
      });
  }

  revokeOAuthToken(id: string) {
    this.isLoading = true;

    this.oauthService
      .revokeToken({ id })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => (this.oauthTokens = this.oauthTokens.filter(token => token.id !== id)),
        error: (error: unknown) => this.errorHandlerService.handle(error)
      });
  }

  copyApiLink(): void {
    this.notyService.success(this.translateService.instant('notyService.apiLinkCopied'), 2000);
  }

  isOAuthTokenExpired(token: OAuthToken): boolean {
    return token.expires_at && new Date(token.expires_at).getTime() < Date.now();
  }

  getOAuthTokenPermissionsDescription(token: OAuthToken): string {
    const permissions = Array.from(
      new Set(
        (token.granted_scopes || []).map(scope => {
          const normalizedScope = (scope || '').trim().toLowerCase();
          const translationKey = `settings.oauthApplications.scopePermissionsInline.${normalizedScope}`;
          const translation = this.translateService.instant(translationKey);

          return translation !== translationKey ? translation : normalizedScope;
        })
      )
    ).filter(Boolean);

    return this.translateService.instant('settings.oauthApplications.can', { permissions: this.joinWithConjunction(permissions) });
  }

  private joinWithConjunction(values: string[]): string {
    if (values.length === 0) {
      return '';
    }

    if (values.length === 1) {
      return values[0];
    }

    const conjunction = this.translateService.instant('settings.oauthApplications.and');

    if (values.length === 2) {
      return `${values[0]} ${conjunction} ${values[1]}`;
    }

    return `${values.slice(0, -1).join(', ')} ${conjunction} ${values[values.length - 1]}`;
  }
}
